'use client'

import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { cn } from "@/lib/utils";
import TruckLayoutTab from './TruckLayoutTab';
import 'react-tabs/style/react-tabs.css';
import Footer from "../footer";
import EditBanner from "../common-components/EditBanner";
import { showSuccessToast, showErrorToast } from "@/components/toast/toast";
import { Skeleton } from "@/components/ui/skeleton";

// Skeleton loader for truck layout
const TruckLayoutSkeleton = () => (
  <div className="p-6 space-y-8">
    {/* Tab list skeleton */}
    <div className="flex items-center gap-2 mb-6">
      {Array(3).fill(0).map((_, i) => (
        <Skeleton key={i} className="h-10 w-40 rounded-md" />
      ))}
    </div>

    {/* Layout skeleton */}
    <div className="space-y-6">
      <div className="flex justify-between">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-8 w-32" />
      </div>

      {/* Truck layout skeleton */}
      <div className="grid grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>

      {/* Form fields skeleton */}
      <div className="space-y-4 mt-8">
        <Skeleton className="h-6 w-40" />
        <div className="grid grid-cols-2 gap-6">
          {Array(6).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>

      {/* Tables skeleton */}
      <div className="space-y-4 mt-8">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-60 w-full" />
      </div>
    </div>
  </div>
);

interface ShipTestTruckLayoutProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
}

export default function ShipTestTruckLayout({
  projectId,
  isOffline,
  onOfflineSubmit
}: ShipTestTruckLayoutProps) {
  const { shippingModes } = useSelector((state: RootState) => state.define);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const roadShippingModes = shippingModes.filter((sm) => sm.transitMode === 'Truck Air ride' || sm.transitMode === 'Truck leaf spring');

  const handleTabChange: any = useCallback(async (index: number) => {
    if (hasUnsavedChanges) {
      await saveCurrentTab();
    }
    setActiveTabIndex(index);
  }, [hasUnsavedChanges]);

  const saveCurrentTab = useCallback(async () => {
    const currentMode = roadShippingModes[activeTabIndex];
    const form = document.getElementById(`postShipTruckLayout-${currentMode?.id}`);
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        (submitButton as HTMLButtonElement).click();
      }
    }
    setHasUnsavedChanges(false);
  }, [activeTabIndex, roadShippingModes]);

  const handleFormChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const handleSubmit = async () => {
    // if (isOffline && onOfflineSubmit) {
    //   await onOfflineSubmit(values);
    //   return;
    // }

    try {
      for (let mode of roadShippingModes) {
        const form = document.getElementById(`postShipTruckLayout-${mode?.id}`);
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton) {
            (submitButton as HTMLButtonElement).click();
          }
        }
      }
      setHasUnsavedChanges(false);
      setIsLoading(false);
      // showSuccessToast("Ship test data saved successfully");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      showErrorToast("Failed to save ship test data");
    }
  };

  useEffect(() => {
    if (isOffline) {
      setIsEditing(true)
    }
  }, [isOffline]);

  // if (isLoading) {
  //   return (
  //     <div className="h-[calc(100vh-140px)] overflow-y-scroll pb-20">
  //       <TruckLayoutSkeleton />
  //     </div>
  //   );
  // }
  console.log(isLoading, 'isLoading')
  return (
    <div className="h-[calc(100vh-140px)] overflow-y-scroll pb-20">
      {!isOffline && <div className="px-4 mt-2">
        <EditBanner
        stage={'truckLayout'}
        palletId={1}
        component={'Ship Test Truck Layout'}
        onEditStateChange={setIsEditing}
      />
      </div>}
      <div className={`${isEditing ? "" : "pointer-events-none"}`}>
        {roadShippingModes.length > 0 ? (
          <Tabs forceRenderTabPanel className="flex flex-col gap-6 p-5" onSelect={handleTabChange}>
            <TabList className="flex items-center gap-2">
              {roadShippingModes.map((mode, i) => (
                <Tab
                  key={i}
                  className="text-placeholder hover:bg-blue hover:text-white cursor-pointer px-4 py-2 rounded-md"
                  selectedClassName="bg-blue text-white"
                >
                  Shipping Mode {mode.mode}
                </Tab>
              ))}
            </TabList>

            {roadShippingModes.map((mode, i) => (
              <TabPanel key={i}>
                <TruckLayoutTab
                  setIsLoading={setIsLoading}
                  shippingMode={{ id: mode.id, mode: mode.mode }}
                  onFormChange={handleFormChange}
                  hasUnsavedChanges={hasUnsavedChanges}
                  saveCurrentForm={saveCurrentTab}
                  isOffline={isOffline || false}
                />
              </TabPanel>
            ))}
          </Tabs>
        ) : (
          <div className="text-center text-blue">No Shipping modes found with Transit mode as Truck Air ride or Truck leaf spring</div>
        )}
      </div>
      <Footer
        isLoading={isLoading}
        onSubmit={handleSubmit}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  );
}
